import { fabric } from 'fabric';
import { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

/** Шаг сетки в пикселях холста при zoom = 1. Snap в E4-F2 берёт эту же константу. */
export const GRID_STEP_PX = 20;
const GRID_OBJECT_NAME = 'grid';
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_BUTTON_FACTOR = 1.1;
const LINE_DELTA_PX = 16;

export type ObjectCanvasView = {
  zoom: number;
  x: number;
  y: number;
};

type CanvasApi = {
  zoomBy: (factor: number) => void;
};

type WorldBounds = {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
};

function clampZoom(value: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

/** Подпись единственного этажа. Пустой или битый floors[0] не бросает исключение. */
export function readFloorLabel(floors: unknown): string {
  if (!Array.isArray(floors) || floors.length === 0) {
    return 'Этаж 1';
  }

  const floor = floors[0];
  if (!floor || typeof floor !== 'object') {
    return 'Этаж 1';
  }

  const name = (floor as { name?: unknown }).name;
  if (typeof name !== 'string') {
    return 'Этаж 1';
  }

  const trimmed = name.trim();
  return trimmed || 'Этаж 1';
}

function readView(canvas: fabric.Canvas): ObjectCanvasView {
  const viewport = canvas.viewportTransform ?? [1, 0, 0, 1, 0, 0];
  return {
    zoom: canvas.getZoom(),
    x: viewport[4] ?? 0,
    y: viewport[5] ?? 0,
  };
}

/** deltaY колеса в пикселях. Firefox часто присылает строки, не пиксели. */
export function wheelDeltaPixels(event: WheelEvent, pageSize: number): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * LINE_DELTA_PX;
  }

  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return event.deltaY * pageSize;
  }

  return event.deltaY;
}

function worldBounds(canvas: fabric.Canvas): WorldBounds {
  const zoom = canvas.getZoom() || 1;
  const viewport = canvas.viewportTransform ?? [1, 0, 0, 1, 0, 0];
  const width = canvas.getWidth();
  const height = canvas.getHeight();
  return {
    x0: (0 - (viewport[4] ?? 0)) / zoom,
    y0: (0 - (viewport[5] ?? 0)) / zoom,
    x1: (width - (viewport[4] ?? 0)) / zoom,
    y1: (height - (viewport[5] ?? 0)) / zoom,
  };
}

function boundsCover(view: WorldBounds, built: WorldBounds | null): boolean {
  if (!built) {
    return false;
  }

  const margin = GRID_STEP_PX * 2;
  return (
    view.x0 >= built.x0 + margin &&
    view.y0 >= built.y0 + margin &&
    view.x1 <= built.x1 - margin &&
    view.y1 <= built.y1 - margin
  );
}

function removeGrid(canvas: fabric.Canvas) {
  canvas
    .getObjects()
    .filter((object) => object.name === GRID_OBJECT_NAME)
    .forEach((object) => canvas.remove(object));
}

/** 1 px на экране. strokeUniform не компенсирует zoom холста в viewport. */
function gridStrokeWidth(zoom: number): number {
  const safeZoom = zoom > 0 ? zoom : 1;
  return 1 / safeZoom;
}

function updateGridStroke(canvas: fabric.Canvas) {
  const width = gridStrokeWidth(canvas.getZoom());
  const lines = canvas.getObjects().filter((object) => object.name === GRID_OBJECT_NAME);
  if (lines.length === 0) {
    return;
  }

  lines.forEach((line) => line.set({ strokeWidth: width }));
  canvas.requestRenderAll();
}

/**
 * Линии в координатах сцены. Подложку E4-F5 класть в backgroundImage:
 * Fabric рисует его под объектами. Стены — canvas.add(), они остаются над сеткой.
 */
function drawGrid(canvas: fabric.Canvas, visible: boolean): WorldBounds | null {
  const renderOnAddRemove = canvas.renderOnAddRemove;
  canvas.renderOnAddRemove = false;
  removeGrid(canvas);

  if (!visible) {
    canvas.renderOnAddRemove = renderOnAddRemove;
    canvas.requestRenderAll();
    return null;
  }

  const view = worldBounds(canvas);
  const padX = view.x1 - view.x0;
  const padY = view.y1 - view.y0;
  const left = view.x0 - padX;
  const top = view.y0 - padY;
  const right = view.x1 + padX;
  const bottom = view.y1 + padY;
  const startX = Math.floor(left / GRID_STEP_PX) * GRID_STEP_PX;
  const startY = Math.floor(top / GRID_STEP_PX) * GRID_STEP_PX;
  const lineOptions = {
    stroke: '#d1d5db',
    strokeWidth: gridStrokeWidth(canvas.getZoom()),
    strokeUniform: true,
    selectable: false,
    evented: false,
    excludeFromExport: true,
    objectCaching: false,
    name: GRID_OBJECT_NAME,
  };

  for (let x = startX; x <= right; x += GRID_STEP_PX) {
    canvas.insertAt(new fabric.Line([x, top, x, bottom], lineOptions), 0, false);
  }

  for (let y = startY; y <= bottom; y += GRID_STEP_PX) {
    canvas.insertAt(new fabric.Line([left, y, right, y], lineOptions), 0, false);
  }

  canvas.renderOnAddRemove = renderOnAddRemove;
  canvas.requestRenderAll();
  return { x0: left, y0: top, x1: right, y1: bottom };
}

function setCanvasCursor(canvas: fabric.Canvas, cursor: string) {
  canvas.defaultCursor = cursor;
  canvas.hoverCursor = cursor;
  canvas.setCursor(cursor);
}

export function ObjectCanvas({
  floorLabel,
  view,
  onViewChange,
  viewOnly = false,
}: {
  floorLabel: string;
  view: ObjectCanvasView;
  onViewChange: (view: ObjectCanvasView) => void;
  viewOnly?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<CanvasApi | null>(null);
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const spaceRef = useRef(false);
  const panningRef = useRef(false);
  const stopPanRef = useRef<(() => void) | null>(null);
  const showGridRef = useRef(true);
  const viewOnlyRef = useRef(viewOnly);
  const onViewChangeRef = useRef(onViewChange);
  const initialViewRef = useRef(view);
  const drawGridRef = useRef<((visible: boolean) => void) | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [zoom, setZoom] = useState(view.zoom);
  const [pan, setPan] = useState({ x: view.x, y: view.y });

  showGridRef.current = showGrid;
  viewOnlyRef.current = viewOnly;
  onViewChangeRef.current = onViewChange;

  function cursorForCanvas(): string {
    if (panningRef.current) {
      return 'grabbing';
    }

    return spaceRef.current ? 'grab' : 'default';
  }

  useHotkeys(
    'space',
    (event) => {
      event.preventDefault();
      spaceRef.current = true;
      const canvas = canvasRef.current;
      if (canvas && !panningRef.current) {
        setCanvasCursor(canvas, 'grab');
      }
    },
    { keydown: true, keyup: false, preventDefault: true },
  );

  useHotkeys(
    'space',
    () => {
      spaceRef.current = false;
      stopPanRef.current?.();
      const canvas = canvasRef.current;
      if (canvas) {
        setCanvasCursor(canvas, cursorForCanvas());
      }
    },
    { keydown: false, keyup: true },
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    const canvasHost: HTMLDivElement = host;
    canvasHost.replaceChildren();
    const element = document.createElement('canvas');
    canvasHost.appendChild(element);

    const canvas = new fabric.Canvas(element, {
      selection: false,
      fireMiddleClick: true,
      defaultCursor: 'default',
      hoverCursor: 'default',
      backgroundColor: 'transparent',
    });
    canvasRef.current = canvas;

    const initialView = initialViewRef.current;
    canvas.setViewportTransform([initialView.zoom, 0, 0, initialView.zoom, initialView.x, initialView.y]);

    let panning = false;
    let lastX = 0;
    let lastY = 0;
    let gridBounds: WorldBounds | null = null;

    function publish() {
      const nextView = readView(canvas);
      setZoom(nextView.zoom);
      setPan({ x: nextView.x, y: nextView.y });
      onViewChangeRef.current(nextView);
    }

    function refreshCursor() {
      setCanvasCursor(canvas, panningRef.current ? 'grabbing' : spaceRef.current ? 'grab' : 'default');
    }

    function ensureGrid(force: boolean) {
      const visible = showGridRef.current;
      if (!visible) {
        if (gridBounds !== null) {
          gridBounds = drawGrid(canvas, false);
        }
        return;
      }

      if (!force && boundsCover(worldBounds(canvas), gridBounds)) {
        return;
      }

      gridBounds = drawGrid(canvas, true);
    }

    function resize() {
      const width = canvasHost.clientWidth;
      const height = canvasHost.clientHeight;
      if (width <= 0 || height <= 0) {
        return;
      }

      canvas.setDimensions({ width, height });
      canvas.calcOffset();
      ensureGrid(true);
    }

    function stopPan() {
      if (!panning) {
        refreshCursor();
        return;
      }

      panning = false;
      panningRef.current = false;
      refreshCursor();
      ensureGrid(false);
    }

    function onMouseMove(event: MouseEvent) {
      if (!panning) {
        return;
      }

      const viewport = canvas.viewportTransform;
      if (!viewport) {
        return;
      }

      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      canvas.setViewportTransform([
        viewport[0],
        viewport[1],
        viewport[2],
        viewport[3],
        viewport[4] + dx,
        viewport[5] + dy,
      ]);
      refreshCursor();
      ensureGrid(false);
      publish();
    }

    function onBlur() {
      spaceRef.current = false;
      stopPan();
    }

    canvas.on('mouse:down', (event) => {
      const panWithMiddle = event.e.button === 1;
      const panWithSpace = event.e.button === 0 && spaceRef.current;
      if (panWithMiddle || panWithSpace) {
        event.e.preventDefault();
        panning = true;
        panningRef.current = true;
        lastX = event.e.clientX;
        lastY = event.e.clientY;
        refreshCursor();
        return;
      }

      if (viewOnlyRef.current) {
        return;
      }
    });

    canvas.on('mouse:wheel', (event) => {
      event.e.preventDefault();
      event.e.stopPropagation();
      const deltaY = wheelDeltaPixels(event.e, canvas.getHeight() || 1);
      const nextZoom = clampZoom(canvas.getZoom() * 0.999 ** deltaY);
      canvas.zoomToPoint(new fabric.Point(event.e.offsetX, event.e.offsetY), nextZoom);
      ensureGrid(false);
      updateGridStroke(canvas);
      publish();
    });

    apiRef.current = {
      zoomBy(factor: number) {
        const nextZoom = clampZoom(canvas.getZoom() * factor);
        canvas.zoomToPoint(new fabric.Point(canvas.getWidth() / 2, canvas.getHeight() / 2), nextZoom);
        ensureGrid(false);
        updateGridStroke(canvas);
        publish();
      },
    };
    stopPanRef.current = stopPan;
    drawGridRef.current = (visible: boolean) => {
      showGridRef.current = visible;
      gridBounds = drawGrid(canvas, visible);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvasHost);
    resize();
    publish();
    refreshCursor();

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopPan);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopPan);
      window.removeEventListener('blur', onBlur);
      observer.disconnect();
      apiRef.current = null;
      stopPanRef.current = null;
      drawGridRef.current = null;
      canvasRef.current = null;
      canvas.dispose();
      canvasHost.replaceChildren();
    };
  }, []);

  useEffect(() => {
    drawGridRef.current?.(showGrid);
  }, [showGrid]);

  const zoomPercent = Math.round(zoom * 100);
  const zoomOutDisabled = zoom <= MIN_ZOOM + 0.001;
  const zoomInDisabled = zoom >= MAX_ZOOM - 0.001;

  return (
    <div className="object-canvas-layout">
      <div className="object-canvas-toolbar">
        <span data-floor-label={floorLabel}>Этаж: {floorLabel}</span>
        <span style={{ color: '#6b7280' }}>Zoom</span>
        <button
          type="button"
          className="btn"
          aria-label="Уменьшить"
          disabled={zoomOutDisabled}
          onClick={() => apiRef.current?.zoomBy(1 / ZOOM_BUTTON_FACTOR)}
        >
          −
        </button>
        <span aria-live="polite" data-zoom={zoomPercent} style={{ minWidth: '48px', textAlign: 'center' }}>
          {zoomPercent}%
        </span>
        <button
          type="button"
          className="btn"
          aria-label="Увеличить"
          disabled={zoomInDisabled}
          onClick={() => apiRef.current?.zoomBy(ZOOM_BUTTON_FACTOR)}
        >
          +
        </button>
        <button
          type="button"
          className={showGrid ? 'btn object-canvas-toolbar__grid' : 'btn'}
          aria-pressed={showGrid}
          onClick={() => setShowGrid((current) => !current)}
        >
          Сетка: {showGrid ? 'Вкл' : 'Выкл'}
        </button>
      </div>

      <div
        className="object-canvas"
        data-object-canvas
        data-grid={showGrid ? 'on' : 'off'}
        data-edits={viewOnly ? 'locked' : 'open'}
        data-pan-x={Math.round(pan.x)}
        data-pan-y={Math.round(pan.y)}
      >
        <div ref={hostRef} className="object-canvas__host" />
      </div>
    </div>
  );
}
